import { I18nProvider, useLocalePreference, type UseLocalePreferenceResult } from '@ecom/i18n';
import { createContext, useContext, type ReactElement, type ReactNode } from 'react';

/**
 * Wraps `useLocalePreference()` (the localStorage-backed hook,
 * `@ecom/i18n`'s own — no new persistence mechanism invented here) in a
 * context, the same division of responsibility `SessionProvider`/
 * `useSession()` already establishes for auth state: called ONCE, at the
 * app root, so `I18nProvider` (which needs `locale` as a prop) and
 * `Topbar` (which needs `setLocale` for the switcher) read the SAME
 * instance rather than two independently-initialised ones that could
 * desync — the identical risk `use-theme.ts`'s own header already warns
 * about for `useTheme()`.
 */
const LocaleContext = createContext<UseLocalePreferenceResult | undefined>(undefined);

export function LocaleProvider({ children }: { children: ReactNode }): ReactElement {
  const value = useLocalePreference();

  return (
    <LocaleContext.Provider value={value}>
      <I18nProvider locale={value.locale}>{children}</I18nProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): UseLocalePreferenceResult {
  const context = useContext(LocaleContext);

  if (context === undefined) {
    throw new Error('useLocale() was called outside a <LocaleProvider>.');
  }

  return context;
}
